/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useStoreContext } from '@/contexts/StoreContext';
import { useCallback } from 'react';

export const useAuthService = () => {
  const { antflowProcessApiEndpoint } = useStoreContext();
  const authService = antflowProcessApiEndpoint.getAuthService();

  const getValidToken = useCallback(async (): Promise<string | null> => {
    const token = authService.getToken();
    if (!authService.isExpired()) {
      return token;
    } else {
      try {
        await authService.refreshToken();
        return authService.getToken();
      } catch (error) {
        console.error('!Failed to refresh token:', error);
        return null;
      }
    }
  }, [authService]);

  const injectedToken = useCallback(
    async (link?: string) => {
      if (!link) {
        return '#';
      }
      const token = await getValidToken();
      const url = new URL(link);
      url.searchParams.set('access_token', token ?? '');
      return url.toString();
    },
    [getValidToken]
  );

  return { getValidToken, injectedToken };
};
